import { Box, Button, Checkbox, Collapse, Divider, Flex, FormControl, FormHelperText, Grid, Input, Spacer, Text, Textarea, useDisclosure, useToast } from "@chakra-ui/react";
import { faFile, faPaperclip, faTags } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { formatDate } from "date-fns";
import LoremIpsum from "react-lorem-ipsum";
import { ICategory, IDossierRequest, RequestStatus, pollTypeToString } from "../types";
import { useRef, useState } from "react";
import { manageDossierRequest } from "../API/dossier";
import { CheckBox } from "@syncfusion/ej2/buttons";
import { downloadFile } from "../API/file";

export interface DossierRequestCardProps {
    dossier:IDossierRequest;
    categories:ICategory[];
    handle_callback:Function;
}

const DossierRequestCard: React.FC<DossierRequestCardProps> = ({dossier, categories, handle_callback}) => {
    const { isOpen, onOpen, onClose, onToggle } = useDisclosure();
    const categoriesString = categories.map((category:ICategory) => category.name).join(", ");
    const reason = useRef<HTMLTextAreaElement>(null);
    const toast = useToast();
    const [isBureau, setIsBureau] = useState<boolean>(false);
    const [submitting, setSubmitting] = useState<boolean>(false);

    const download_file = (file_id:string) => {
        downloadFile(file_id).then((resp) => {
          const contentDisposition = resp.headers['content-disposition'];
          const filename = contentDisposition.split(';')[1].split('filename')[1].split('=')[1].trim();
          const url = window.URL.createObjectURL(new Blob([resp.data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', filename);
          document.body.appendChild(link);
          link.click();
        });
    }

    const open_card_wrapper = () => {
        onToggle();
    }

    const getLabel = (status:RequestStatus) => {
        switch(status) {
            case RequestStatus.Accepted:
                return <Box bgColor={"green.500"} color={"white"} width={'fit-content'} pe={2} ps={2} pt={1} pb={1} fontSize={'xs'} fontWeight={'800'} border={"1pt solid #266d47"} position={'absolute'} right={"-10px"} top={"-10px"}>
                ACCEPTÉ
            </Box>
            case RequestStatus.Refused:
                return <Box bgColor={"red.500"} color={"white"} width={'fit-content'} pe={2} ps={2} pt={1} pb={1} fontSize={'xs'} fontWeight={'800'} border={"1pt solid #610002"} position={'absolute'} right={"-10px"} top={"-10px"}>
                REFUSÉ
            </Box>
            default:
                return <Box bgColor={"gray.500"} color={"white"} width={'fit-content'} pe={2} ps={2} pt={1} pb={1} fontSize={'xs'} fontWeight={'800'} border={"1pt solid #373e49"} position={'absolute'} right={"-10px"} top={"-10px"}>
                EN ATTENTE
            </Box> 
        }
    }

    return <Box mb={5} onClick={isOpen ? () => {}: open_card_wrapper} cursor={isOpen ? "default" : "pointer"} width={"100%"} bgColor={"white"} p={6} pb={5} border={"1pt solid #e2e2dd"} position={'relative'}>

    {getLabel(dossier.status)}

    <Flex>
        <Text letterSpacing={"-.01rem"} lineHeight={"1.2"} fontWeight={'700'} fontSize={'lg'} color={"#00707f"}>{dossier.title}</Text>
        <Spacer />
        <Text cursor={'pointer'} onClick={() => { if (isOpen) onClose();}} color={"#00707f"} fontSize={"xs"} fontWeight={900} pt={2} pe={2}>
            {isOpen ? "FERMER" : "CONSULTER"}
        </Text>
    </Flex>
    <Text fontWeight={'400'} fontSize={'sm'} color={"#7f8591"}>Demandé le {formatDate(dossier.createdAt ?? new Date(), "dd/MM/yyyy à HH:mm")}</Text>

    {dossier.high_priority &&
        <Text pt={2} fontWeight={'700'} fontSize={'sm'} color={"#ba0105"}>HAUTE PRIORITÉ DEMANDÉE</Text>
    }

    <Collapse in={isOpen} animateOpacity>
        <Divider mt={5} mb={5} />

        {dossier.deadline &&
        <Text>
            Clôture du dossier demandée pour le <Text as={"span"} fontWeight={'700'}>{formatDate(dossier.deadline ?? new Date(), "dd/MM/yyyy à HH:mm")}</Text>
        </Text>
        }
            
        {!dossier.deadline &&
        <Text>
            Clôture manuelle demandée (pas de deadline indiquée)
        </Text>
        }

        <Box mt={8}>
            <Text mb={3} ms={3} color={"#004851"} fontWeight={'800'} fontSize={'sm'}>DESCRIPTION</Text>
            <Box mb={5} height={'4px'} bgColor={"#004851"} width={'100%'} />
        </Box>

        <Box ms={2} mb={2}>
            Catégories: <Text as={"span"} fontWeight={'700'} color={"#004851"}>{categoriesString}</Text>
        </Box>

        <Box border={"1pt solid #e2e2dd"} bgColor={"white"} p={5} textAlign={"justify"}>
            <Box dangerouslySetInnerHTML={{__html: dossier.description}} />
        </Box>

        <Box ps={2} pt={2}>
            <Text fontSize={'sm'}><FontAwesomeIcon style={{marginRight:"3px", position:"relative", top:"2px"}} icon={faTags} /> <Text as={"span"} fontWeight={'700'}>Mots-clés: </Text> {dossier.tags.join(", ")}</Text>
        </Box>

        {dossier.attached_files.length > 0 &&
        <Box p={3} mt={2}>
            <Text fontSize={'lg'} fontWeight={'700'} color={"#314e89"} mb={2}>
                <FontAwesomeIcon icon={faPaperclip} style={{marginRight:"5px"}} />Fichiers joints
            </Text>
            <Grid templateColumns='repeat(4, 2fr)' gap={6}>
                {dossier.attached_files.map((attachment, index) => {
                    return <Box key={index} cursor={'pointer'} bgColor={'white'} p={3} textAlign={'center'} border={"1pt solid #e2e2dd"} onClick={() => {download_file(attachment.id)}}>
                        <Text fontWeight={'400'} ><FontAwesomeIcon icon={faFile} style={{marginRight:"5px"}} />{attachment.name}</Text>
                    </Box>
                })}
            </Grid>
        </Box>
        }

        
        <Box mt={8}>
            <Text mb={3} ms={3} color={"#004851"} fontWeight={'800'} fontSize={'sm'}>SONDAGES</Text>
            <Box mb={5} height={'4px'} bgColor={"#004851"} width={'100%'} />
        </Box>

        {dossier.polls.length > 0 && dossier.polls.map((poll, index) => {
            return <Box key={index} border={"1pt solid #e2e2dd"} p={3} mt={3} rounded={"md"}>
                <Flex>
                    <Text fontWeight={'800'} fontSize={'md'}>{poll.title}</Text>
                    <Spacer />
                </Flex>
                <Text>{poll.description}</Text>
                <Text>{pollTypeToString(poll.type)}</Text>
                <Text>{poll.options.join(", ")}</Text>
                <Text>{formatDate(poll.deadline, "dd/MM/yyyy à HH:mm")}</Text>
            </Box>
        })}
        
        {dossier.polls.length == 0 && <Text>Aucun sondage n'a été ajouté à cette demande</Text>}

        
        {dossier.high_priority && <>
            <Box mt={8}>
                <Text mb={3} ms={3} color={"#004851"} fontWeight={'800'} fontSize={'sm'}>HAUTE PRIORITÉ</Text>
                <Box mb={5} height={'4px'} bgColor={"#004851"} width={'100%'} />
            </Box>

            <Text mb={3}>
                Cette demande a été marquée comme <Text as={"span"} fontWeight={'700'} color={"#ba0105"}>haute priorité</Text>. Voici votre justification :
            </Text>

            <Box border={"1pt solid #e2e2dd"} bgColor={"white"} p={5}>
                {dossier.priority_reason}
            </Box>
        </>}

        <Box mt={8}>
            <Text mb={3} ms={3} color={"#5b257d"} fontWeight={'800'} fontSize={'sm'}>RÉPONSE DU BUREAU</Text>
            <Box mb={5} height={'4px'} bgColor={"#5b257d"} width={'100%'} />
        </Box>
        
        {dossier.status == RequestStatus.Pending &&
            <Box border={"1pt solid #e2e2dd"} bgColor={"white"} p={5}>
                <Text>
                    En attente de réponse du Bureau...
                </Text>
            </Box>
        }
        {dossier.status == RequestStatus.Refused &&
            <Box border={"1pt solid #e2e2dd"} bgColor={"white"} p={5}>
                <Text color={"red.500"} fontWeight={700}>
                    Demande refusée le {formatDate(dossier.status_date ?? new Date(), "dd/MM/yyyy à HH:mm")} pour la raison suivante :
                </Text>
                <Text>{dossier.status_reason}</Text>
            </Box>
        }
        {dossier.status == RequestStatus.Accepted &&
            <Box border={"1pt solid #e2e2dd"} bgColor={"white"} p={5}>
                <Text color={"green.500"} fontWeight={700}>
                    Demande acceptée le {formatDate(dossier.status_date ?? new Date(), "dd/MM/yyyy à HH:mm")}.
                </Text>
            </Box>
        }
    </Collapse>
</Box>
}

export default DossierRequestCard;